/**
 * Profile Page
 *
 * Self-service profile management for users.
 * Allows users to edit their own profile and change password.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  User,
  Mail,
  Camera,
  Lock,
  Shield,
  MapPin,
  Loader2,
  X,
  Check,
  Building2,
} from 'lucide-react';
import { useAuth, type RoleType } from '../contexts/AuthContext';
import {
  getMyProfile,
  updateMyProfile,
  changePassword,
  uploadAvatar,
} from '../services/users.service';
import LoadingPlaceholder from '../components/LoadingPlaceholder';
import { useDateFormatter } from '../hooks/useDateFormatter';

interface UserRoleWithDetails {
  id: string;
  role: RoleType;
  merchant_id: string;
  location_id: string | null;
  merchants?: {
    id: string;
    business_name: string;
    display_name: string;
  };
  locations?: {
    id: string;
    location_name: string;
  } | null;
}

interface ProfileData {
  id: string;
  email?: string | null;
  full_name: string | null;
  avatar_url?: string | null;
  is_platform_admin: boolean | null;
  roles: UserRoleWithDetails[];
  last_sign_in_at?: string | null;
  password_set?: boolean;
}

const ROLE_LABELS: Record<RoleType, string> = {
  admin: 'Admin',
  manager: 'Manager',
  operator: 'Operator',
  viewer: 'Viewer',
  equipment_check: 'Equipment check',
};

export default function Profile() {
  const { user, merchantId } = useAuth();
  const { t } = useTranslation();
  const { formatDateTimeCompact } = useDateFormatter();

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [fullName, setFullName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Password modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Avatar upload
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  async function loadProfile() {
    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await getMyProfile();

    if (fetchError) {
      setError(typeof fetchError === 'string' ? fetchError : t('profile.failedToLoadProfile'));
      setLoading(false);
      return;
    }

    if (data) {
      setProfile(data as unknown as ProfileData);
      setFullName(data.full_name || '');
    }

    setLoading(false);
  }

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveSuccess(false);

    const { error: updateError } = await updateMyProfile({
      full_name: fullName,
    });

    if (updateError) {
      setError(typeof updateError === 'string' ? updateError : t('profile.failedToSaveProfile'));
      setSaving(false);
      return;
    }

    setSaveSuccess(true);
    setSaving(false);

    // Hide success message after 3 seconds
    setTimeout(() => setSaveSuccess(false), 3000);

    // Reload profile
    loadProfile();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(t('profile.pleaseSelectImageFile'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('profile.imageSizeLimit'));
      return;
    }

    setUploadingAvatar(true);
    setError(null);

    const { error: uploadError } = await uploadAvatar(file);

    if (uploadError) {
      setError(typeof uploadError === 'string' ? uploadError : t('profile.failedToUploadAvatar'));
      setUploadingAvatar(false);
      return;
    }

    setUploadingAvatar(false);
    loadProfile();
  };

  const getInitials = (name: string | null, email: string | null) => {
    if (name) {
      const parts = name.split(' ');
      return parts.length > 1
        ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
        : name.substring(0, 2).toUpperCase();
    }
    return email ? email.substring(0, 2).toUpperCase() : '??';
  };

  if (loading) {
    return (
      <div className="space-y-2xl animate-fade-in">
        <div className="page-header">
          <div className="page-header-title">
            <h1 className="text-3xl font-bold text-primary mb-sm">{t('profile.myProfile')}</h1>
            <p className="text-secondary">{t('profile.manageAccountSettings')}</p>
          </div>
        </div>
        <div className="card">
          <LoadingPlaceholder />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2xl animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-title">
          <h1 className="text-3xl font-bold text-primary mb-sm">My Profile</h1>
          <p className="text-secondary">Manage your account settings</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-xl">
        {/* Profile Card */}
        <div className="lg:col-span-2 space-y-xl">
          {/* Profile Form */}
          <div className="card">
            <h2 className="text-lg font-semibold text-primary mb-lg flex items-center gap-sm">
              <User className="w-5 h-5" />
              {t('profile.profileInformation')}
            </h2>

            {error && (
              <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg mb-lg">
                <p className="text-sm text-danger">{error}</p>
              </div>
            )}

            {saveSuccess && (
              <div className="p-md bg-success/10 border-l-4 border-success rounded-lg mb-lg flex items-center gap-sm">
                <Check className="w-4 h-4 text-success" />
                <p className="text-sm text-success">{t('profile.profileSavedSuccessfully')}</p>
              </div>
            )}

            <form onSubmit={handleSaveProfile} className="space-y-lg">
              {/* Avatar */}
              <div className="flex items-center gap-lg">
                <div className="relative">
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={t('profile.profilePhoto')}
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-blue/10 flex items-center justify-center text-blue text-2xl font-semibold">
                      {getInitials(profile?.full_name || null, profile?.email || null)}
                    </div>
                  )}
                  <label
                    className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-blue text-white flex items-center justify-center cursor-pointer hover:bg-blue/80 transition-colors"
                    title={t('profile.changeAvatar')}
                  >
                    {uploadingAvatar ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                      disabled={uploadingAvatar}
                    />
                  </label>
                </div>
                <div>
                  <p className="text-sm text-secondary">{t('profile.profilePhoto')}</p>
                  <p className="text-xs text-secondary mt-xs">{t('profile.photoFormats')}</p>
                </div>
              </div>

              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-medium text-primary mb-xs">
                  <Mail className="w-4 h-4 inline mr-xs" />
                  {t('profile.emailAddress')}
                </label>
                <p className="text-primary py-sm">{profile?.email || user?.email || t('profile.noEmail')}</p>
                <p className="text-xs text-secondary mt-xs">{t('profile.emailCannotBeChanged')}</p>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-primary mb-xs">
                  <User className="w-4 h-4 inline mr-xs" />
                  {t('profile.fullName')}
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t('profile.fullNamePlaceholder')}
                  className="input w-full"
                />
              </div>

              {/* Save Button */}
              <div className="pt-lg border-t border-primary">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('common.saving')}
                    </>
                  ) : (
                    t('profile.saveChanges')
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Security */}
          <div className="card">
            <h2 className="text-lg font-semibold text-primary mb-lg flex items-center gap-sm">
              <Lock className="w-5 h-5" />
              {t('profile.security')}
            </h2>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-primary">{t('profile.password')}</p>
                <p className="text-sm text-secondary">{t('profile.changeYourAccountPassword')}</p>
              </div>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="btn btn-secondary"
              >
                {t('profile.changePassword')}
              </button>
            </div>
          </div>
        </div>

        {/* Access Info Card */}
        <div className="space-y-xl">
          <div className="card">
            <h2 className="text-lg font-semibold text-primary mb-lg flex items-center gap-sm">
              <Shield className="w-5 h-5" />
              {t('profile.myAccess')}
            </h2>

            {profile?.is_platform_admin && (
              <div className="p-md bg-blue/10 border border-blue/20 rounded-lg mb-lg">
                <p className="font-medium text-blue flex items-center gap-sm">
                  <Shield className="w-4 h-4" />
                  {t('profile.platformAdministrator')}
                </p>
                <p className="text-sm text-secondary mt-xs">
                  {t('profile.fullAccessDescription')}
                </p>
              </div>
            )}

            {profile?.roles && profile.roles.length > 0 ? (
              <div className="space-y-md">
                {profile.roles.map((role) => (
                  <div
                    key={role.id}
                    className="p-md border border-primary rounded-lg"
                  >
                    <div className="flex items-center gap-sm mb-sm">
                      <Building2 className="w-4 h-4 text-secondary" />
                      <span className="font-medium text-primary">
                        {role.merchants?.display_name || role.merchants?.business_name || t('profile.unknownMerchant')}
                      </span>
                      {role.merchant_id === merchantId && profile.roles.length > 1 && (
                        <span className="badge badge-success">{t('profile.activeMerchant')}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-md text-sm text-secondary">
                      <span className="badge badge-info">{ROLE_LABELS[role.role]}</span>
                      <span className="flex items-center gap-xs">
                        <MapPin className="w-3 h-3" />
                        {role.locations?.location_name || t('profile.allLocations')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-secondary">{t('profile.noMerchantAccess')}</p>
            )}
          </div>

          {/* Last Login */}
          {profile?.last_sign_in_at && (
            <div className="card">
              <h2 className="text-lg font-semibold text-primary mb-md">{t('profile.lastLogin')}</h2>
              <p className="text-secondary">
                {formatDateTimeCompact(profile.last_sign_in_at)}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <ChangePasswordModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
        />
      )}
    </div>
  );
}

// Change Password Modal Component
function ChangePasswordModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate passwords
    if (newPassword.length < 8) {
      setError(t('profile.passwordMinLength'));
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('profile.passwordsDoNotMatch'));
      setLoading(false);
      return;
    }

    const { error: changeError } = await changePassword(newPassword);

    if (changeError) {
      setError(typeof changeError === 'string' ? changeError : t('profile.failedToChangePassword'));
      setLoading(false);
      return;
    }

    setSuccess(true);
    setLoading(false);

    // Close modal after success
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white rounded-lg shadow-2xl max-w-md w-full"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-lg border-b border-primary">
          <div className="flex items-center gap-md">
            <Lock className="w-6 h-6 text-blue" />
            <h2 className="text-xl font-bold text-primary">{t('profile.changePassword')}</h2>
          </div>
          <button onClick={onClose} className="btn-icon" title={t('profile.close')}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-lg space-y-lg">
          {error && (
            <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-md bg-success/10 border-l-4 border-success rounded-lg flex items-center gap-sm">
              <Check className="w-4 h-4 text-success" />
              <p className="text-sm text-success">{t('profile.passwordChangedSuccessfully')}</p>
            </div>
          )}

          {!success && (
            <>
              <div>
                <label className="block text-sm font-medium text-primary mb-xs">
                  {t('profile.newPassword')}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('profile.enterNewPassword')}
                  className="input w-full"
                  required
                  minLength={8}
                />
                <p className="text-xs text-secondary mt-xs">{t('profile.minimumCharacters')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-xs">
                  {t('profile.confirmPassword')}
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('profile.confirmNewPassword')}
                  className="input w-full"
                  required
                />
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-md pt-lg border-t border-primary">
                <button type="button" onClick={onClose} className="btn btn-ghost">
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('profile.changing')}
                    </>
                  ) : (
                    t('profile.changePassword')
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>,
    document.body
  );
}
